import React, { useMemo, useState } from 'react';
import { Check, FileJson, X } from 'lucide-react';
import { type ColumnDef } from '@tanstack/react-table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@roger/ui-components';
import BillingPeriodCell from '../BillingPeriodCell';
import EOEPreviewPopup from '../EOEPreviewPopup';
import { BillingDataTable } from '../BillingDataTable';
import { BillingActionsCell } from '../BillingActionsCell';
import { draftBillingCycleNoteContext } from '../billingCycleNoteContext';
import { PatientCell } from '../PatientCell';
import { InsuranceCell } from '../InsuranceCell';
import { getDraftBillingCycles } from '../../../services/api';
import type { GetBillingCyclesResponse } from '../../../services/api/types.gen';
import { formatDateToMMDDYYYY } from '../../../utils/dateUtils';
import { ClaimAgeCell } from '../ClaimAgeCell';
import { claimAgeDays } from '../claimAge';
import { PAYMENT_OASIS_MISSING_MESSAGE } from '../../../constants/billing';
import { useBillingCycles } from '../../../hooks/useBillingCycles';
import { useBillingTableQuery } from '../../../hooks/useBillingTableQuery';

type ReadinessStatus = 'pass' | 'fail' | 'na';

interface ReadinessItem {
  label: string;
  status: ReadinessStatus;
  message?: string;
}

// eslint-disable-next-line react-refresh/only-export-components -- exported for unit testing
export function getReadinessItems(
  entry: GetBillingCyclesResponse
): ReadinessItem[] {
  const mbiStatus: ReadinessStatus = !entry.is_mbi_required
    ? 'na'
    : entry.is_mbi_valid
      ? 'pass'
      : 'fail';

  const items: ReadinessItem[] = [
    { label: 'OASIS', status: entry.all_oasis_correct_state ? 'pass' : 'fail' },
  ];

  if (entry.payment_oasis_status === 'missing') {
    items.push({
      label: 'Payment OASIS',
      status: 'fail',
      message: PAYMENT_OASIS_MISSING_MESSAGE,
    });
  }

  items.push(
    {
      // SQL readiness HIPPS gate (inputs), not grouper success — full code is
      // preview-only via hipps-preview.
      label: 'HIPPS inputs',
      status: entry.has_computable_hipps_inputs ? 'pass' : 'fail',
    },
    { label: 'Visits', status: entry.all_visits_completed ? 'pass' : 'fail' },
    { label: 'Orders', status: entry.all_orders_signed ? 'pass' : 'fail' },
    { label: 'MBI', status: mbiStatus }
  );

  if (entry.is_pcr_tracking_number_required) {
    items.push({
      label: 'PCR tracking #',
      status: entry.is_pcr_tracking_number_valid ? 'pass' : 'fail',
    });
  }

  if (entry.is_authorization_required) {
    items.push({
      label: 'Authorization',
      status: entry.is_authorization_valid ? 'pass' : 'fail',
    });
  }

  return items;
}

function isClaimReady(items: ReadinessItem[]): boolean {
  return items.every(item => item.status !== 'fail');
}

const ReadinessIcon: React.FC<{ status: ReadinessStatus }> = ({ status }) => {
  if (status === 'na') {
    return <span className='text-muted-foreground text-xs'>N/A</span>;
  }
  if (status === 'pass') {
    return <Check className='h-4 w-4 text-green-600' />;
  }
  return <X className='h-4 w-4 text-red-600' />;
};

const ReadinessCell: React.FC<{
  entry: GetBillingCyclesResponse;
  onPreview: (entry: GetBillingCyclesResponse) => void;
}> = ({ entry, onPreview }) => {
  const readinessItems = getReadinessItems(entry);
  // Every row on this tab is Not Ready by membership. Some failing gates are
  // not in the checklist (e.g. has_billable_visits), so the displayed items can
  // all pass while the cycle still needs investigation — always keep preview
  // reachable.
  const checklistClean = isClaimReady(readinessItems);

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type='button'
            onClick={() => onPreview(entry)}
            className='flex items-center justify-center cursor-pointer w-fit rounded-sm outline-offset-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring'
            aria-label='Open claim preview'
          >
            <FileJson
              className={`h-4 w-4 ${checklistClean ? 'text-green-600' : 'text-red-600'}`}
            />
          </button>
        </TooltipTrigger>
        <TooltipContent side='right' className='p-3'>
          <ul className='space-y-1.5'>
            {readinessItems.map(item => (
              <li key={item.label} className='space-y-0.5'>
                <div className='flex items-center justify-between gap-4'>
                  <span className='text-xs'>{item.label}</span>
                  <ReadinessIcon status={item.status} />
                </div>
                {item.message ? (
                  <p className='text-xs leading-snug text-red-700'>
                    {item.message}
                  </p>
                ) : null}
              </li>
            ))}
          </ul>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

interface EOENotReadyTabProps {
  onCountsInvalidated?: () => Promise<void> | void;
}

const EOENotReadyTab: React.FC<EOENotReadyTabProps> = ({
  onCountsInvalidated,
}) => {
  const [draftPopupCycle, setDraftPopupCycle] =
    useState<GetBillingCyclesResponse | null>(null);

  const query = useBillingTableQuery('not-ready', {
    sortKeyByColumnId: { billing_period: 'start_date' },
  });
  const { data, isLoading, isRefreshing, total, totalPages, loadData } =
    useBillingCycles<GetBillingCyclesResponse, { ready_for_review: boolean }>({
      tabId: 'not-ready',
      fetchFn: getDraftBillingCycles,
      fetchPayload: { ready_for_review: false, ...query.toParams() },
      errorMessage: 'Could not fetch data for the "Not Ready" tab.',
      getId: item => item.billing_cycle_id,
      getName: item => item.patient_name,
    });

  const columns = useMemo<ColumnDef<GetBillingCyclesResponse>[]>(
    () => [
      {
        id: 'patient_name',
        accessorKey: 'patient_name',
        header: 'Patient',
        enableSorting: true,
        cell: ({ row }) => (
          <PatientCell
            name={row.original.patient_name}
            mrn={row.original.patient_mrn}
          />
        ),
      },
      {
        id: 'clinic_name',
        accessorKey: 'clinic_name',
        header: 'Clinic',
        enableSorting: true,
      },
      {
        id: 'insurance_name',
        accessorKey: 'insurance_name',
        header: 'Insurance',
        enableSorting: true,
        cell: ({ row }) => (
          <InsuranceCell
            insuranceName={row.original.insurance_name}
            mbi={row.original.mbi}
            isMedicare={row.original.is_medicare}
          />
        ),
      },
      {
        id: 'billing_period',
        header: 'Billing Period',
        accessorFn: row => row.start_date,
        enableSorting: true,
        cell: ({ row }) => (
          <BillingPeriodCell
            patientId={row.original.patient_id}
            episodeId={row.original.episode_id}
            startDate={formatDateToMMDDYYYY(row.original.start_date)}
            endDate={formatDateToMMDDYYYY(row.original.end_date)}
          />
        ),
      },
      {
        id: 'claim_age',
        header: 'Claim Age',
        accessorFn: row => claimAgeDays(row.end_date) ?? -1,
        enableSorting: true,
        cell: ({ row }) => (
          <ClaimAgeCell
            periodEnd={row.original.end_date}
            createdAt={row.original.created_at}
          />
        ),
      },
      {
        id: 'actions',
        header: () => <div className='text-right'>Actions</div>,
        enableSorting: false,
        cell: ({ row }) => (
          <BillingActionsCell
            billingCycleId={row.original.billing_cycle_id}
            showDownload={false}
            noteProps={{
              ...draftBillingCycleNoteContext(row.original, false),
              patientName: row.original.patient_name,
            }}
            leading={
              <ReadinessCell
                entry={row.original}
                onPreview={setDraftPopupCycle}
              />
            }
          />
        ),
      },
    ],
    []
  );

  return (
    <>
      <BillingDataTable
        data={data}
        columns={columns}
        getRowId={row => row.billing_cycle_id}
        query={query}
        total={total}
        totalPages={totalPages}
        isLoading={isLoading}
        isRefreshing={isRefreshing}
      />
      <EOEPreviewPopup
        billingCycle={draftPopupCycle}
        onOpenChange={open => !open && setDraftPopupCycle(null)}
        onBillingDataChanged={() => {
          void loadData();
          void onCountsInvalidated?.();
        }}
      />
    </>
  );
};

export default EOENotReadyTab;
